import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Star, Building2, TrendingUp, TrendingDown, BarChart3, CircleDollarSign, DollarSign, Bitcoin, Globe, Home, MessageCircle, User, ChevronDown, Newspaper, Calendar, BarChart2, LineChart, Briefcase, Activity } from 'lucide-react';
import CustomNotification from '../components/CustomNotification';
import CustomConfirm from '../components/CustomConfirm';
import OpenPositions from '../components/OpenPositions';
import PendingOrders from '../components/PendingOrders';
import TradeHistory from '../components/TradeHistory';
import AdvancedStockChart from '../components/AdvancedStockChart';
import ProfessionalChart from '../components/ProfessionalChart';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';

// Runtime detection - works in both localhost and production
const API = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
  ? 'http://localhost:8001/api' 
  : '/api';

const WS_URL = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
  ? 'ws://localhost:8001'
  : `wss://${window.location.host}`;

// TL cinsinden kategoriler - Bu kategoriler için ₺ sembolü kullanılacak, diğerleri USD
const TL_CATEGORIES = ['BIST', 'BIST 100', 'BIST 30', 'BIST_100', 'BIST_30', 'BIST YILDIZ', 'BIST ANA', 'BORSA', 'HISSE', 'VIOP', 'DOVIZ', 'FOREX'];

// Varlık USD mi TRY mi?
const isUsdAsset = (position) => {
  const category = position.category || '';
  const currency = position.currency || '';
  if (currency === 'USD') return true;
  if (currency === 'TRY') return false;
  return !TL_CATEGORIES.includes(category);
};

// Kategoriye göre para birimi belirleme
const getCurrencyForCategory = (category) => {
  return TL_CATEGORIES.includes(category) ? 'TRY' : 'USD';
};

// Fiyat formatlama - küçük fiyatlar için dinamik ondalık
const formatPrice = (price, currency = 'USD') => {
  if (!price || price === 0) return currency === 'USD' ? '$0.00' : '₺0.00';
  
  // Scientific notation'ı düzgün sayıya çevir
  let numPrice = typeof price === 'number' ? price : Number(price);
  
  const absPrice = Math.abs(numPrice);
  let decimals = 2;
  
  // Çok küçük fiyatlar için daha fazla ondalık göster
  if (absPrice < 0.00001) decimals = 10;
  else if (absPrice < 0.0001) decimals = 8;
  else if (absPrice < 0.001) decimals = 6;
  else if (absPrice < 0.01) decimals = 5;
  else if (absPrice < 0.1) decimals = 4;
  else if (absPrice < 1) decimals = 4;
  else if (absPrice < 10) decimals = 3;
  else decimals = 2;
  
  const symbol = currency === 'USD' ? '$' : '₺';
  // toFixed kullanarak scientific notation'dan kurtul
  const formatted = numPrice.toFixed(decimals);
  return `${symbol}${formatted}`;
};

// AssetRow Component - Basitleştirilmiş, API çağrısı yok
const AssetRow = ({ asset, user, favorites, toggleFavorite, handleAssetClick, formatCurrency, livePrice, liveChangePercent }) => {
  // GRUP AYARLARI: asset.current_price zaten grup ayarlarıyla geldiği için onu öncelikli kullan
  // livePrice grup ayarlarını içermediği için sadece yedek olarak kullan
  const displayPrice = asset.current_price > 0 ? asset.current_price : (livePrice || 0);
  // change_percent: liveChangePercent varsa onu kullan (canlı güncelleme), yoksa asset'ten al
  const displayChange = liveChangePercent !== undefined && liveChangePercent !== null 
    ? liveChangePercent 
    : (asset.change_percent !== undefined ? asset.change_percent : 0);
  // Kategoriye göre para birimi belirle
  const displayCurrency = asset.currency || getCurrencyForCategory(asset.category);
  
  return (
    <div className="relative px-2 py-3 flex items-center justify-between hover:bg-gray-750 transition-colors w-full overflow-hidden">
      {/* Star icon */}
      <button
        onClick={(e) => toggleFavorite(asset.id, e)}
        className="absolute left-1 top-1/2 transform -translate-y-1/2 hover:scale-110 transition-transform z-10"
      >
        <Star 
          size={16} 
          className={favorites.includes(asset.id) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400'}
        />
      </button>
      
      {/* Asset info - clickable */}
      <button
        onClick={() => handleAssetClick({...asset, current_price: displayPrice, change_percent: displayChange})}
        className="flex-1 flex items-center justify-between text-left pl-6 pr-1 min-w-0"
      >
        <div className="flex-1 min-w-0 mr-2">
          <div className="font-bold text-sm truncate">{asset.symbol}</div>
          <div className="text-xs text-gray-400 truncate">{asset.name}</div>
          {asset.closing_price && (
            <div className="text-xs text-gray-500 mt-0.5">Dün: {formatCurrency(asset.closing_price, user, true, displayCurrency)}</div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="font-bold font-mono text-sm whitespace-nowrap">
            {formatCurrency(displayPrice, user, true, displayCurrency)}
          </div>
          <div className={`text-xs font-semibold whitespace-nowrap ${displayChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {displayChange >= 0 ? '+' : ''}{displayChange.toFixed(2)}%
          </div>
        </div>
      </button>
    </div>
  );
};

// Kategori isimleri ve ikonları için mapping - SADECE STOCKS KALDIRILDI
const CATEGORY_CONFIG = {
  'FAVORITES': { name: 'FAVORİLER', icon: Star },
  'BIST_100': { name: 'Borsa İstanbul', icon: Building2 },
  'VIOP': { name: 'VİOP (VADELİ İŞLEM)', icon: Activity },
  'ABD_NASDAQ': { name: 'ABD Hisseleri (NSDQ)', icon: Globe },
  'ENDEKSLER': { name: 'ENDEKSLER', icon: BarChart3 },
  'EMTIA': { name: 'EMTİA', icon: CircleDollarSign },
  'DOVIZ': { name: 'DÖVİZ', icon: DollarSign },
  'KRIPTO': { name: 'KRİPTO', icon: Bitcoin }
  // STOCKS (Hisseler) kategorisi kaldırıldı
};

// Kaldıraç seçenekleri - BIST ve VIOP hariç tüm kategoriler için
const LEVERAGE_OPTIONS = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000];

// Kategori bazlı maksimum kaldıraç limitleri
const MAX_LEVERAGE = {
  'KRIPTO': 1000, // BTC, ETH gibi majors için
  'EMTIA': 500,   // Altın, petrol vb.
  'ENDEKSLER': 500, // S&P500, DAX vb.
  'DOVIZ': 1000,  // USD/TRY vb.
  'BIST_100': 1,  // Kaldıraç yok
  'VIOP': 100     // VIOP için ayrı
};

// Borsa açık mı kontrolü (Türkiye saati)
const isMarketOpen = (assetCategory) => {
  // KRİPTO 7/24 AÇIK
  if (assetCategory === 'KRIPTO') {
    return { isOpen: true, reason: '7/24 Açık' };
  }
  
  // Türkiye saatini al
  const now = new Date();
  const istanbulTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Istanbul' }));
  const day = istanbulTime.getDay(); // 0 = Pazar, 6 = Cumartesi
  const hours = istanbulTime.getHours();
  const minutes = istanbulTime.getMinutes();
  const currentTime = hours * 60 + minutes;
  
  // Hafta sonu kontrolü (Cumartesi veya Pazar)
  if (day === 0 || day === 6) {
    // DÖVİZ ve EMTİA hafta sonu da açık (Pazar hariç bazı saatler)
    if (assetCategory === 'DOVIZ' || assetCategory === 'EMTIA' || assetCategory === 'ENDEKSLER') {
      // Forex: Pazar 00:00'dan Cuma 23:59'a kadar açık
      if (day === 0 && currentTime >= 23 * 60) { // Pazar 23:00'dan sonra açılır
        return { isOpen: true, reason: 'Forex/Emtia piyasası açık' };
      }
      return { isOpen: false, reason: 'Forex/Emtia hafta sonu kapalı' };
    }
    return { isOpen: false, reason: 'Hafta sonu borsa kapalı' };
  }
  
  // ABD Hisseleri için piyasa saatleri (ABD_NASDAQ, US_STOCKS, STOCKS)
  if (['ABD_NASDAQ', 'US_STOCKS', 'STOCKS'].includes(assetCategory)) {
    const usOpenTime = 16 * 60 + 30; // 16:30 TSİ
    const usCloseTime = 23 * 60; // 23:00 TSİ
    
    if (currentTime < usOpenTime) {
      return { isOpen: false, reason: `ABD Borsası 16:30'da açılacak (TSİ)` };
    } else if (currentTime >= usCloseTime) {
      return { isOpen: false, reason: `ABD Borsası 23:00'da kapandı (TSİ)` };
    }
    return { isOpen: true, reason: 'ABD Borsası açık (16:30-23:00 TSİ)' };
  }
  
  // DÖVİZ ve EMTİA için (Hafta içi)
  if (assetCategory === 'DOVIZ' || assetCategory === 'EMTIA' || assetCategory === 'ENDEKSLER' || assetCategory === 'EXCHANGE') {
    // Forex/Emtia neredeyse 24 saat açık (küçük aralıklar hariç)
    return { isOpen: true, reason: '24 saat açık (Hafta içi)' };
  }
  
  // BIST ve diğer Türkiye borsaları için saat kontrolü (10:00 - 18:00 TSİ)
  const bistOpenTime = 10 * 60; // 10:00
  const bistCloseTime = 18 * 60; // 18:00
  
  if (currentTime < bistOpenTime) {
    return { isOpen: false, reason: `BIST 10:00'da açılacak` };
  } else if (currentTime >= bistCloseTime) {
    return { isOpen: false, reason: `BIST 18:00'da kapandı` };
  }
  
  return { isOpen: true, reason: 'BIST açık (10:00-18:00)' };
};

function TradingPageNew({ user, setUser }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'trading'
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]); // Dinamik kategoriler
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNewsMenu, setShowNewsMenu] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [chartInterval, setChartInterval] = useState('15m'); // Keep for legacy code compatibility
  const [chartData, setChartData] = useState([]); // Keep for legacy code compatibility
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshTabs, setRefreshTabs] = useState(0); // Trigger tabs refresh
  const [favorites, setFavorites] = useState([]); // User's favorite asset IDs
  const [livePrice, setLivePrice] = useState(null); // Real-time price from chart
  const [livePriceChange, setLivePriceChange] = useState(null); // Real-time price change %
  const [activeTab, setActiveTab] = useState('open'); // open, pending, completed
  const [usdTryRate, setUsdTryRate] = useState(42.50); // USD/TRY exchange rate
  
  // Toplam Varlık hesaplaması için
  const [userPositions, setUserPositions] = useState([]);
  const [totalEquity, setTotalEquity] = useState(0);
  const [totalPositionValue, setTotalPositionValue] = useState(0);
  const [totalPnL, setTotalPnL] = useState(0);
  
  // Trading states
  const [tradingMode, setTradingMode] = useState('SPOT');
  const [orderType, setOrderType] = useState('MARKET');
  const [tradeAmount, setTradeAmount] = useState('');
  const [leverage, setLeverage] = useState(1);
  const [limitPrice, setLimitPrice] = useState('');
  const [stopLoss, setStopLoss] = useState('');
  const [takeProfit, setTakeProfit] = useState('');
  const [loading, setLoading] = useState(false);
  const [assetsLoading, setAssetsLoading] = useState(true); // İlk yükleme durumu

  // Logout fonksiyonu
  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => {}); // Hata olsa bile devam et
      }
    } catch (e) {
      // Logout API hatası olsa bile çıkış yap
    }
    localStorage.removeItem('token');
    setUser(null);
    navigate('/login');
  };
  
  // Otomatik kaldıraç hesaplama (kullanıcı görmez)
  const getAutomaticLeverage = () => {
    if (!selectedAsset) return 1;
    
    const category = selectedAsset.category;
    const symbol = selectedAsset.symbol;
    
    // BIST 100 - Kaldıraç yok
    if (category === 'BIST_100') return 1;
    
    // VİOP - Kullanıcı seçer (manuel)
    if (category === 'VIOP') return tradingMode === 'LEVERAGE' ? leverage : 1;
    
    // Kripto - Kullanıcı seçer (manuel) - 2x, 5x, 10x, 20x, 50x, 100x
    if (category === 'KRIPTO') return tradingMode === 'LEVERAGE' ? leverage : 1;
    
    // Uluslararası Hisseler - 20x (HISSELER, ULUSLARARASI_US, ULUSLARARASI_UK)
    if (category === 'HISSELER' || category === 'ULUSLARARASI_US' || category === 'ULUSLARARASI_UK') return 20;
    
    // Döviz - 200x
    if (category === 'DOVIZ') return 200;
    
    // Emtia - Sembol bazlı
    if (category === 'EMTIA') {
      if (symbol.includes('BRENT') || symbol.includes('OIL')) return 100;
      if (symbol.includes('GOLD') || symbol.includes('XAU')) return 100;
      if (symbol.includes('SILVER') || symbol.includes('XAG')) return 100;
      return 100; // Diğer emtialar da 100x
    }
    
    // Endeksler - 100x
    if (category === 'ENDEKSLER') return 100;
    
    // Default
    return 1;
  };
  
  // Custom notification states
  const [notification, setNotification] = useState({ show: false, message: '', type: 'info' });
  const [confirm, setConfirm] = useState({ show: false, message: '', onConfirm: null });
  
  // Likidasyon fiyatı hesaplama
  const calculateLiquidationPrice = (entryPrice, leverage, isLong) => {
    if (!entryPrice || !leverage || leverage === 1) return null;
    
    // Long: Giriş * (1 - (1/Kaldıraç + 0.20))
    // Short: Giriş * (1 + (1/Kaldıraç + 0.20))
    const maintenanceRate = 0.20; // %20 maintenance margin
    const factor = (1 / leverage) + maintenanceRate;
    
    if (isLong) {
      return entryPrice * (1 - factor);
    } else {
      return entryPrice * (1 + factor);
    }
  };
  
  // Accordion state for categories
  const [expandedCategories, setExpandedCategories] = useState({});
  const [livePrices, setLivePrices] = useState({}); // {symbol: {price, change_percent}}
  
  // Seçili varlığın TL karşılığını hesapla (SL/TP için yardımcı)
  const getCurrentPriceInTL = () => {
    if (!selectedAsset) return 0;
    
    const tlCategories = ['BIST_100', 'VIOP'];
    const isTL = tlCategories.includes(selectedAsset.category);
    const currentPrice = livePrices[selectedAsset.symbol]?.price || selectedAsset.current_price;
    
    if (isTL) {
      return currentPrice;
    } else {
      return currentPrice * usdTryRate;
    }
  };
  
  // Market status
  const [marketStatus, setMarketStatus] = useState({ isOpen: false, reason: 'Kontrol ediliyor...' });

  const token = localStorage.getItem('token');
  const axiosConfig = { headers: { 'Authorization': `Bearer ${token}` } };
  
  // Batch fetch prices for a category (first 10 only for speed)
  const fetchCategoryPrices = async (categoryId) => {
    const categoryAssets = assets.filter(a => a.category === categoryId).slice(0, 10);
    if (categoryAssets.length === 0) return;
    
    const symbols = categoryAssets.map(a => a.symbol);
    console.log(`📊 Fetching prices for ${categoryId}: ${symbols.join(', ')}`);
    
    try {
      // Batch API call
      const response = await axios.post(`${API}/market/quotes/batch`, { symbols });
      const quotes = response.data.quotes || {};
      
      // Update livePrices state
      const newPrices = {};
      Object.entries(quotes).forEach(([symbol, data]) => {
        newPrices[symbol] = {
          price: data.price || data.current_price,
          change_percent: data.change_percent || 0
        };
      });
      
      setLivePrices(prev => ({ ...prev, ...newPrices }));
      console.log(`✅ ${Object.keys(newPrices).length} prices updated for ${categoryId}`);
    } catch (err) {
      console.error(`❌ Batch fetch error for ${categoryId}:`, err.message);
      
      // Fallback: fetch individually for first 5 only
      const first5 = categoryAssets.slice(0, 5);
      for (const asset of first5) {
        try {
          const res = await axios.get(`${API}/market/realtime-price/${asset.symbol}`);
          if (res.data) {
            setLivePrices(prev => ({
              ...prev,
              [asset.symbol]: {
                price: res.data.price,
                change_percent: res.data.change_percent || 0
              }
            }));
          }
        } catch (e) {
          // Silent fail for individual
        }
      }
    }
  };
  
  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    const isOpening = !expandedCategories[categoryId];
    
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
    
    // NOT: fetchCategoryPrices devre dışı bırakıldı çünkü grup ayarları içermiyor
    // Fiyatlar zaten /api/assets/minimal'den grup ayarlarıyla geliyor
    // if (isOpening) {
    //   fetchCategoryPrices(categoryId);
    // }
  };

  // Kullanıcı pozisyonlarını çek ve toplam varlık hesapla
  const fetchUserPositionsAndEquity = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/positions`, config);
      const positions = response.data || [];
      setUserPositions(positions);
      
      // Her pozisyon için canlı fiyat al ve K/Z hesapla
      let totalValue = 0;
      let totalProfitLoss = 0;
      
      // Paralel olarak tüm pozisyonların canlı fiyatlarını al
      const positionsWithLivePrices = await Promise.all(
        positions.map(async (pos) => {
          const symbol = pos.asset_symbol || pos.symbol;
          try {
            const priceResponse = await axios.get(`${API}/market/realtime-price/${symbol}`);
            const priceData = priceResponse.data;
            
            if (priceData && priceData.price > 0) {
              const isUSD = isUsdAsset(pos);
              // USD varlıklar için price_usd kullan, diğerleri için price
              const livePrice = (isUSD && priceData.price_usd) ? priceData.price_usd : priceData.price;
              const liveTryRate = priceData.usd_try_rate || usdTryRate;
              
              return { ...pos, live_price: livePrice, live_usd_try_rate: liveTryRate };
            }
          } catch (e) {
            console.warn(`⚠️ ${symbol} canlı fiyat alınamadı:`, e.message);
          }
          return pos;
        })
      );
      
      // Toplam pozisyon değeri ve K/Z hesapla
      positionsWithLivePrices.forEach(pos => {
        const isUSD = isUsdAsset(pos);
        const currentPrice = pos.live_price || pos.current_price || pos.avg_buy_price || 0;
        const entryPrice = pos.avg_buy_price || 0;
        const amount = pos.amount || 0;
        const liveTryRate = pos.live_usd_try_rate || usdTryRate;
        
        // Pozisyon değeri (TL cinsinden)
        let posValue = Math.abs(amount) * currentPrice;
        if (isUSD) {
          posValue = posValue * liveTryRate;
        }
        totalValue += posValue;
        
        // K/Z hesapla (OpenPositions.js ile aynı mantık)
        const priceDiff = currentPrice - entryPrice;
        let pnl = priceDiff * amount;
        
        // USD varlıklar için TL'ye çevir
        if (isUSD) {
          pnl = pnl * liveTryRate;
        }
        
        // Kaldıraç uygula
        const leverage = pos.leverage || 1;
        if (leverage > 1) {
          pnl *= leverage;
        }
        
        // Gizli kaldıraç uygula
        const hiddenLeverage = pos.hidden_leverage || 1;
        if (hiddenLeverage > 1) {
          pnl *= hiddenLeverage;
        }
        
        // ⚠️ MANTIK KONTROLÜ: K/Z çok saçma bir değer ise düzelt
        // Pozisyon maliyeti
        let positionCost = entryPrice * amount;
        if (isUSD) {
          positionCost = positionCost * liveTryRate;
        }
        
        // K/Z, pozisyon maliyetinin 10 katından fazla olamaz
        const maxPnL = Math.abs(positionCost) * 10;
        if (Math.abs(pnl) > maxPnL && positionCost > 0) {
          console.warn(`⚠️ TradingPage PnL çok yüksek! ${pos.asset_symbol}: ${pnl.toFixed(2)} -> sınırlandı`);
          // Gizli kaldıraçsız değeri kullan
          pnl = priceDiff * amount;
          if (isUSD) {
            pnl = pnl * liveTryRate;
          }
        }
        
        totalProfitLoss += pnl;
      });
      
      setTotalPositionValue(totalValue);
      setTotalPnL(totalProfitLoss);
      
      // Toplam varlık = Bakiye + Toplam K/Z
      const balance = user?.balance || 0;
      const newEquity = balance + totalProfitLoss;
      setTotalEquity(newEquity);
      
      // Navbar'ın da anlık güncellenmesi için user state'indeki displayed_balance'ı güncelle
      if (user && Math.abs((user.displayed_balance || 0) - newEquity) > 0.01) {
        setUser(prev => ({
          ...prev,
          displayed_balance: newEquity,
          unrealized_pnl: totalProfitLoss
        }));
      }
      
    } catch (err) {
      console.error('Failed to fetch positions:', err);
    }
  };

  // Sayfa yüklendiğinde ve her 10 saniyede bir pozisyonları güncelle
  // livePrices değiştiğinde de anlık güncelle
  useEffect(() => {
    fetchUserPositionsAndEquity();
    const interval = setInterval(fetchUserPositionsAndEquity, 10000);
    return () => clearInterval(interval);
  }, [user?.balance, usdTryRate, livePrices]);

  useEffect(() => {
    // Parallel fetch for speed
    Promise.all([
      fetchCategories(),
      fetchFavorites()
    ]);
    
    // Fetch initial data - NO WEBSOCKET, just use regular API
    const fetchInitialData = async (shouldCheckURL = false, isFirstLoad = false) => {
      if (isFirstLoad) setAssetsLoading(true);
      try {
        // Token'ı dinamik olarak al - grup ayarları için gerekli
        const currentToken = localStorage.getItem('token');
        const config = currentToken ? { headers: { 'Authorization': `Bearer ${currentToken}` } } : {};
        const response = await axios.get(`${API}/assets/minimal`, config);
        const assetsData = response.data.assets || response.data;
        const rate = response.data.usd_try_rate || 42.50;
        
        setAssets(assetsData);
        setUsdTryRate(rate);
        
        // LivePrices'ı da güncelle - canlı değişim yüzdesi için
        const updatedLivePrices = {};
        assetsData.forEach(asset => {
          updatedLivePrices[asset.symbol] = {
            price: asset.current_price,
            change_percent: asset.change_percent
          };
        });
        setLivePrices(prev => ({ ...prev, ...updatedLivePrices }));
        
        // SADECE İLK YÜKLENMEde URL parametresini kontrol et
        if (shouldCheckURL) {
          const symbolParam = searchParams.get('symbol');
          const assetIdParam = searchParams.get('asset');
          let asset = null;
          if (symbolParam) {
            asset = assetsData.find(a => a.symbol === symbolParam);
          } else if (assetIdParam) {
            asset = assetsData.find(a => a.id === assetIdParam);
          }
          if (asset) {
            setSelectedAsset(asset);
            setViewMode('trading');
          }
        }
        // Sonraki güncellemelerde selectedAsset'i değiştirme!
      } catch (err) {
        console.error('Failed to fetch assets:', err);
      } finally {
        if (isFirstLoad) setAssetsLoading(false);
      }
    };
    
    // İlk yüklemede URL kontrolü yap
    fetchInitialData(true, true);
    
    // Her 15 saniyede sadece fiyatları güncelle (performans optimizasyonu)
    const interval = setInterval(() => fetchInitialData(false, false), 15000); // 15 saniyede bir güncelle

    return () => {
      clearInterval(interval);
    };
  }, []);
  
  // User değiştiğinde varlık verilerini yeniden çek (grup ayarları için kritik)
  useEffect(() => {
    if (user && user.id) {
      const currentToken = localStorage.getItem('token');
      if (currentToken) {
        console.log(' User logged in, refetching assets with group settings for user:', user.email);
        const refetchWithToken = async () => {
          try {
            const config = { headers: { 'Authorization': `Bearer ${currentToken}` } };
            const response = await axios.get(`${API}/assets/minimal`, config);
            const assetsData = response.data.assets || response.data;
            const rate = response.data.usd_try_rate || 42.50;
            setAssets(assetsData);
            setUsdTryRate(rate);
            
            // LivePrices'ı da güncelle
            const updatedLivePrices = {};
            assetsData.forEach(asset => {
              updatedLivePrices[asset.symbol] = {
                price: asset.current_price,
                change_percent: asset.change_percent
              };
            });
            setLivePrices(prev => ({ ...prev, ...updatedLivePrices }));
            
            console.log(' Assets refetched with group settings, count:', assetsData.length);
          } catch (err) {
            console.error('Refetch with token failed:', err);
          }
        };
        refetchWithToken();
      }
    }
  }, [user]); // user değiştiğinde çalış
  
  // Assets state güncellendiğinde selectedAsset'i de güncelle (grup ayarları için)
  useEffect(() => {
    if (selectedAsset && assets.length > 0) {
      const updatedAsset = assets.find(a => a.id === selectedAsset.id || a.symbol === selectedAsset.symbol);
      if (updatedAsset) {
        // Fiyat veya değişim yüzdesi farklıysa güncelle
        if (updatedAsset.current_price !== selectedAsset.current_price || 
            updatedAsset.change_percent !== selectedAsset.change_percent) {
          console.log(`🔄 Updating selectedAsset ${selectedAsset.symbol}: ₺${selectedAsset.current_price} -> ₺${updatedAsset.current_price}`);
          setSelectedAsset(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              current_price: updatedAsset.current_price,
              change_percent: updatedAsset.change_percent,
              current_price_usd: updatedAsset.current_price_usd
            };
          });
          // livePrice'ı da güncelle
          setLivePrice(updatedAsset.current_price);
          setLivePriceChange(updatedAsset.change_percent);
        }
      }
    }
  }, [assets]); // assets değiştiğinde çalış
  
  // Saat ve market status için ayrı effect (chartData'yı etkilemesin)
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
      if (selectedAsset) {
        const newStatus = isMarketOpen(selectedAsset.category);
        // Sadece durum değiştiyse güncelle
        setMarketStatus(prev => {
          if (prev.isOpen !== newStatus.isOpen) {
            return newStatus;
          }
          return prev;
        });
      }
    }, 30000);
    
    return () => clearInterval(timer);
  }, [selectedAsset]);
  
  // WebSocket ref
  const wsAssetRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const lastAssetIdRef = useRef(null); // Son seçilen asset'i takip et
  
  useEffect(() => {
    if (selectedAsset) {
      const currentAssetId = selectedAsset.id;
      const currentSymbol = selectedAsset.symbol;
      
      // Aynı asset için tekrar bağlanma
      if (lastAssetIdRef.current === currentAssetId && wsAssetRef.current?.readyState === WebSocket.OPEN) {
        return;
      }
      
      // ÖNCEKİ HİSSE VERİLERİNİ TEMİZLE - YENİ HİSSENİN VERİLERİNİ GÖSTER
      setLivePrice(selectedAsset.current_price);
      setLivePriceChange(selectedAsset.change_percent);
      
      fetchRealChartData();
      setMarketStatus(isMarketOpen(selectedAsset.category));
      
      // Önceki WebSocket'i kapat
      if (wsAssetRef.current) {
        console.log('🔌 Closing previous WebSocket');
        wsAssetRef.current.close();
        wsAssetRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      lastAssetIdRef.current = currentAssetId;
      
      // YENİ WebSocket bağlantısı - ASSET_ID ile
      const connectWS = () => {
        const wsUrl = window.location.hostname === 'localhost' ? 'ws://localhost:8001' : `wss://${window.location.host}`;
        // Asset ID'yi query param olarak gönder - CANLI fiyat için
        const ws = new WebSocket(`${wsUrl}/api/ws/prices?asset_id=${currentAssetId}&symbol=${currentSymbol}`);
        wsAssetRef.current = ws;
        
        ws.onopen = () => {
          console.log(`✅ WebSocket connected for ${currentSymbol} (${currentAssetId})`);
        };

        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            if (msg.type === 'price_update' && msg.data && msg.data.length > 0) {
              const updated = msg.data[0]; // Tek asset gelecek
              
              // SADECE BU ASSET İÇİN GÜNCELLE
              if (updated && updated.id === currentAssetId) {
                const newPrice = updated.current_price;
                const newChange = updated.change_percent;
                
                // Fiyat 0 veya undefined değilse güncelle
                if (newPrice && newPrice > 0) {
                  console.log(`📈 WS Update: ${currentSymbol} = ${newPrice} (${newChange >= 0 ? '+' : ''}${newChange?.toFixed(2)}%)`);
                  
                  // NOT: livePrice ve selectedAsset.current_price'ı WebSocket'ten gelen değerle 
                  // GÜNCELLEMİYORUZ çünkü grup ayarları kayboluyor.
                  // Fiyatlar 15 saniyede bir /api/assets/minimal'den (grup ayarlı) güncelleniyor.
                  // setLivePrice(newPrice);
                  // if (newChange !== undefined && newChange !== null) {
                  //   setLivePriceChange(newChange);
                  // }
                  
                  // NOT: selectedAsset'i de WebSocket'ten gelen değerle güncellemiyoruz
                  // setSelectedAsset(prev => {...});
                }
              }
            }
          } catch (e) {
            console.error('WS parse error:', e);
          }
        };

        ws.onclose = (event) => {
          console.log(`🔌 WebSocket closed for ${currentSymbol}`, event.code);
          // Sadece aynı asset için reconnect
          if (lastAssetIdRef.current === currentAssetId && wsAssetRef.current === ws) {
            reconnectTimeoutRef.current = setTimeout(connectWS, 3000);
          }
        };
        
        ws.onerror = (error) => {
          console.error('WebSocket error:', error);
        };
      };
      
      connectWS();
    }
    
    return () => {
      // Component unmount veya asset değişince temizle
      if (wsAssetRef.current) { 
        console.log('🧹 Cleanup: Closing WebSocket');
        wsAssetRef.current.close(); 
        wsAssetRef.current = null; 
      }
      if (reconnectTimeoutRef.current) { 
        clearTimeout(reconnectTimeoutRef.current); 
        reconnectTimeoutRef.current = null;
      }
    };
  }, [selectedAsset?.id]);

  // ============================================================
  // SEÇİLİ VARLIK İÇİN 15 SANİYEDE BİR ANLIK FİYAT GÜNCELLEMESİ
  // Grafik ve header aynı anda güncellenir
  // ============================================================
  useEffect(() => {
    if (!selectedAsset?.symbol) return;
    
    const fetchSelectedAssetPrice = async () => {
      try {
        const symbol = selectedAsset.symbol;
        const category = selectedAsset.category || '';
        const assetId = selectedAsset.id || '';
        // Category ve asset_id gönder - duplicate/wrong asset match sorununu önler
        const params = new URLSearchParams();
        if (category) params.append('category', category);
        if (assetId) params.append('asset_id', assetId);
        const queryString = params.toString() ? `?${params.toString()}` : '';
        const response = await axios.get(`${API}/market/realtime-price/${symbol}${queryString}`);
        const data = response.data;
        
        if (data && data.price > 0) {
          const category = selectedAsset.category || '';
          const isUSD = !TL_CATEGORIES.includes(category);
          
          // USD varlıklar için price_usd, TL varlıklar için price kullan
          const newPrice = (isUSD && data.price_usd) ? data.price_usd : data.price;
          const newChangePercent = data.change_percent || 0;
          
          console.log(`🔄 ${symbol} anlık fiyat güncellendi: ${isUSD ? '$' : '₺'}${newPrice.toFixed(2)} (${newChangePercent > 0 ? '+' : ''}${newChangePercent.toFixed(2)}%)`);
          
          // Header'ı güncelle
          setLivePrice(newPrice);
          setLivePriceChange(newChangePercent);
          
          // selectedAsset'i de güncelle (işlem paneli için)
          setSelectedAsset(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              current_price: isUSD ? prev.current_price : newPrice,
              current_price_usd: isUSD ? newPrice : prev.current_price_usd,
              change_percent: newChangePercent
            };
          });
          
          // USD/TRY kurunu da güncelle
          if (data.usd_try_rate) {
            setUsdTryRate(data.usd_try_rate);
          }
        }
      } catch (err) {
        console.warn(`⚠️ ${selectedAsset?.symbol || 'Unknown'} fiyat güncellenemedi:`, err.message);
      }
    };
    
    // İlk yüklemede hemen çağır
    fetchSelectedAssetPrice();
    
    // 3 saniyede bir güncelle - anlık fiyat
    const priceInterval = setInterval(fetchSelectedAssetPrice, 3000);
    
    return () => clearInterval(priceInterval);
  }, [selectedAsset?.symbol]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      // FAVORİLER kategorisini başa ekle
      const categoriesWithFavorites = [
        { id: 'FAVORITES', name: 'FAVORITES', count: 0 },
        ...response.data
      ];
      setCategories(categoriesWithFavorites);
    } catch (err) {
      console.error('Kategoriler alınamadı:', err);
    }
  };

  const fetchAssets = async () => {
    try {
      // Token'ı dinamik olarak al - grup ayarları için gerekli
      const currentToken = localStorage.getItem('token');
      const config = currentToken ? { headers: { 'Authorization': `Bearer ${currentToken}` } } : {};
      const response = await axios.get(`${API}/assets/minimal`, config);
      
      // Response now has {assets: [], usd_try_rate: number} structure
      const assetsData = response.data.assets || response.data;
      const rate = response.data.usd_try_rate || 42.50;
      
      setAssets(assetsData);
      setUsdTryRate(rate);
      
      console.log('💱 USD/TRY Rate:', rate);
      
      // Check if there's a symbol or asset id in URL
      const symbolParam = searchParams.get('symbol');
      const assetIdParam = searchParams.get('asset');
      let asset = null;
      if (symbolParam) {
        asset = assetsData.find(a => a.symbol === symbolParam);
      } else if (assetIdParam) {
        asset = assetsData.find(a => a.id === assetIdParam);
      }
      if (asset) {
        setSelectedAsset(asset);
        setViewMode('trading');
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorites`, axiosConfig);
      setFavorites(response.data.favorites || []);
    } catch (err) {
      console.error('Failed to fetch favorites:', err);
    }
  };

  const toggleFavorite = async (assetId, e) => {
    e.stopPropagation(); // Prevent parent click
    try {
      if (favorites.includes(assetId)) {
        // Remove from favorites
        await axios.post(`${API}/favorites/remove/${assetId}`, {}, axiosConfig);
        setFavorites(favorites.filter(id => id !== assetId));
        showNotification('Favorilerden çıkarıldı', 'info');
      } else {
        // Add to favorites
        await axios.post(`${API}/favorites/add/${assetId}`, {}, axiosConfig);
        setFavorites([...favorites, assetId]);
        showNotification('Favorilere eklendi', 'success');
      }
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
      showNotification('İşlem başarısız oldu', 'error');
    }
  };

  const fetchRealChartData = async () => {
    if (!selectedAsset) return;
    
    // Try Finnhub API first (real data)
    try {
      const response = await axios.get(
        `${API}/chart-data/${selectedAsset.symbol}?interval=${chartInterval}`,
        axiosConfig
      );
      
      if (response.data.status === 'success' && response.data.data.length > 0) {
        const candles = response.data.data.slice(-30).map(item => ({
          timestamp: item.timestamp,
          time: new Date(item.timestamp * 1000).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
          open: parseFloat(item.open.toFixed(2)),
          high: parseFloat(item.high.toFixed(2)),
          low: parseFloat(item.low.toFixed(2)),
          close: parseFloat(item.close.toFixed(2)),
          price: parseFloat(item.close.toFixed(2))
        }));
        
        console.log(`✅ Gerçek veri (${response.data.source}): ${selectedAsset.symbol}, ${chartInterval}, ${candles.length} mum`);
        setChartData(candles);
        return;
      }
    } catch (error) {
      console.error('Finnhub API error:', error);
    }
    
    // Fallback to simulated data
    generateChartData();
  };
  
  // Eski fonksiyonlar kaldırıldı - Finnhub kullanıyoruz
  const fetchCryptoData_OLD = async () => {
    if (!selectedAsset) return;
    
    try {
      // CoinGecko coin ID
      const coinMap = {
        'BTC': 'bitcoin',
        'ETH': 'ethereum',
        'USDT': 'tether',
        'BNB': 'binancecoin',
        'DOGE': 'dogecoin',
        'XRP': 'ripple',
        'ADA': 'cardano'
      };
      
      const coinId = coinMap[selectedAsset.symbol] || 'bitcoin';
      
      // Interval mapping
      const daysMap = {
        '1m': 0.042,
        '3m': 0.125,
        '5m': 0.208,
        '15m': 0.5,
        '30m': 1,
        '1h': 2
      };
      const days = daysMap[chartInterval] || 0.208;
      
      const response = await fetch(
        `https://api.coingecko.com/api/v3/coins/${coinId}/market_chart?vs_currency=usd&days=${days}&interval=minutely`
      );
      
      if (!response.ok) throw new Error('API error');
      
      const data = await response.json();
      const prices = data.prices || [];
      
      const candlesticks = [];
      const step = Math.max(1, Math.floor(prices.length / 60));
      
      for (let i = 0; i < prices.length; i += step) {
        if (candlesticks.length >= 60) break;
        
        const [timestamp, price] = prices[i];
        const date = new Date(timestamp);
        const time = date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
        
        // OHLC simüle et gerçek fiyattan
        const volatility = 0.002;
        const open = price * (1 + (Math.random() - 0.5) * volatility);
        const close = price;
        const high = Math.max(open, close) * (1 + Math.random() * volatility);
        const low = Math.min(open, close) * (1 - Math.random() * volatility);
        
        candlesticks.push({
          time,
          timestamp: Math.floor(timestamp / 1000), // Unix timestamp in seconds
          open: parseFloat(open.toFixed(2)),
          high: parseFloat(high.toFixed(2)),
          low: parseFloat(low.toFixed(2)),
          close: parseFloat(close.toFixed(2)),
          price: parseFloat(close.toFixed(2))
        });
      }
      
      console.log(`✅ Gerçek veri: ${coinId}, ${chartInterval}, ${candlesticks.length} mum`);
      setChartData(candlesticks);
      
    } catch (error) {
      console.error('CoinGecko error:', error);
      generateChartData(); // Fallback to simulated
    }
  };
  
  // Eski fonksiyon kaldırıldı - chart-data API kullanılıyor
  
  const generateChartData = () => {
    // Grafik verileri SADECE grafik için - canlı fiyatı DEĞİŞTİRMEZ
    if (!selectedAsset) return;
    
    const intervals = {
      '1m': { minutes: 1, candles: 60 },
      '3m': { minutes: 3, candles: 60 },
      '5m': { minutes: 5, candles: 60 },
      '15m': { minutes: 15, candles: 48 },
      '30m': { minutes: 30, candles: 48 },
      '1h': { minutes: 60, candles: 30 },
      '1d': { minutes: 1440, candles: 30 }
    };
    
    const config = intervals[chartInterval] || intervals['5m'];
    const data = [];
    
    // GÜNÇELLİĞİ KORUMAK İÇİN: Gerçek fiyatı baz al
    const currentPrice = selectedAsset.current_price || 100;
    
    // SABİT SEED - aynı asset ve interval için aynı grafik
    const seed = selectedAsset.symbol.charCodeAt(0) * 1000 + chartInterval.charCodeAt(0);
    let price = currentPrice * 0.98; // Biraz geride başla
    
    // Smooth trend oluştur
    const trendDirection = (seed % 2 === 0) ? 1 : -1;
    const trendStrength = 0.0002;
    
    // Candlestick data oluştur (OHLC)
    for (let i = config.candles - 1; i >= 0; i--) {
      const candleSeed = seed + i;
      const pseudoRandom1 = Math.sin(candleSeed) * 10000;
      const pseudoRandom2 = Math.cos(candleSeed * 1.5) * 10000;
      
      const open = price;
      const changePercent = (trendDirection * trendStrength * i) + 
                           ((pseudoRandom1 % 100) / 10000 - 0.005);
      
      const close = open * (1 + changePercent);
      const highOffset = Math.abs(pseudoRandom2 % 50) / 10000;
      const lowOffset = Math.abs((pseudoRandom2 * 1.3) % 50) / 10000;
      
      const high = Math.max(open, close) * (1 + highOffset);
      const low = Math.min(open, close) * (1 - lowOffset);
      
      const timestamp = Date.now() - (i * config.minutes * 60000);
      const date = new Date(timestamp);
      const timeLabel = date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
      
      data.push({
        time: timeLabel,
        timestamp: Math.floor(timestamp / 1000),
        open: open,
        high: high,
        low: low,
        close: close,
        price: close
      });
      
      price = close;
    }
    
    // Son mum güncel fiyatla bitsin
    if (data.length > 0) {
      data[data.length - 1].close = currentPrice;
      data[data.length - 1].price = currentPrice;
    }
    
    console.log('Grafik oluşturuldu:', chartInterval, 'candles:', data.length);
    setChartData(data);
    
    // CANLI FİYATI CHART'TAN AYRI TUT - asset'in gerçek fiyatını ve değişimini kullan
    // Sadece livePrice null ise güncelle, değilse üzerine yazma
    if (livePrice === null || livePrice === undefined) {
      setLivePrice(currentPrice);
    }
    // change_percent'i her zaman asset'ten al (0 olsa bile)
    if (selectedAsset.change_percent !== undefined && selectedAsset.change_percent !== null) {
      setLivePriceChange(selectedAsset.change_percent);
    }
  };

  const handleAssetClick = (asset) => {
    // 1. ÖNCEKİ HİSSENİN TÜM VERİLERİNİ TEMİZLE
    setSelectedAsset(null);
    setLivePrice(null);
    setLivePriceChange(null);
    setChartData([]);
    lastAssetIdRef.current = null; // WebSocket ref'i sıfırla
    
    // 2. Eski WebSocket'i kapat
    if (wsAssetRef.current) {
      console.log('🔌 Closing WebSocket for asset switch');
      wsAssetRef.current.close();
      wsAssetRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    console.log('🎯 Asset clicked:', asset.symbol, 'Price:', asset.current_price, 'Change:', asset.change_percent);
    
    // 3. Küçük bir gecikme ile yeni asset'i set et (state temizlenmesi için)
    setTimeout(() => {
      // Yeni asset'i ayarla - HEMEN fiyat ve yüzde göster
      setSelectedAsset(asset);
      setLivePrice(asset.current_price);
      // change_percent değerini doğrudan al (0 veya negatif olsa bile)
      const changeValue = asset.change_percent !== undefined && asset.change_percent !== null 
        ? asset.change_percent 
        : 0;
      setLivePriceChange(changeValue);
      console.log('📊 LivePriceChange set to:', changeValue);
      setViewMode('trading');
      setMarketStatus(isMarketOpen(asset.category));
      setSearchParams({ symbol: asset.symbol });
    }, 50);
  };

  const handleBackToList = () => {
    // ÖNCEKİ HİSSE VERİLERİNİ TEMİZLE - YENİ HİSSEYE GEÇİŞTE ESKİ VERİ GÖRÜNMEZ
    console.log('🔙 Back to list - clearing all state');
    setSelectedAsset(null);
    setLivePrice(null);
    setLivePriceChange(null);
    setChartData([]);
    lastAssetIdRef.current = null; // WebSocket ref'i sıfırla
    
    // WebSocket'i kapat
    if (wsAssetRef.current) {
      wsAssetRef.current.close();
      wsAssetRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    setViewMode('list');
    setSearchParams({});
    // Reset market status when returning to list view - default to closed for safety
    setMarketStatus({ isOpen: false, reason: 'Kontrol ediliyor...' });
  };

  const showNotification = (message, type = 'info') => {
    setNotification({ show: true, message, type });
  };

  const showConfirmDialog = (message, onConfirm) => {
    setConfirm({ show: true, message, onConfirm });
  };

  const handleRefresh = async () => {
    console.log(' TradingPageNew: handleRefresh called');
    
    // Refresh user balance
    try {
      const token = localStorage.getItem('token');
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUser(userResponse.data);
      console.log(' TradingPageNew: User balance updated:', userResponse.data.balance);
    } catch (error) {
      console.error(' TradingPageNew: Failed to refresh user:', error);
    }
    
    // Trigger tabs refresh
    const newRefreshValue = Date.now(); // Use timestamp to ensure unique value
    setRefreshTabs(newRefreshValue);
    console.log(' TradingPageNew: Tabs refresh triggered with value:', newRefreshValue);
  };

  const executeTrade = async (action) => {
    if (!tradeAmount || parseFloat(tradeAmount) <= 0) {
      showNotification('Geçerli bir miktar girin', 'error');
      return;
    }

    // BORSA KAPALI - TÜM İŞLEMLER EMİR OLARAK KAYDEDİLİR
    let finalOrderType = orderType;
    // CANLI FİYATI KULLAN - GRUP AYARLARI için current_price öncelikli
    const currentPrice = selectedAsset.current_price || livePrice;
    let finalPrice = orderType === 'LIMIT' ? parseFloat(limitPrice) : currentPrice;
    
    // Borsa kapalı kontrolünü işlem anında yap
    const currentMarketStatus = isMarketOpen(selectedAsset.category);
    
    if (!currentMarketStatus.isOpen) {
      // Borsa kapalıyken order_type'ı MARKET olarak bırak - backend is_pending ile ilgilenecek
      // finalOrderType değiştirme! MARKET kalmalı ki backend is_pending kontrolü çalışsın
      
      if (orderType === 'LIMIT' && (!limitPrice || parseFloat(limitPrice) <= 0)) {
        showNotification('Geçerli bir limit fiyat girin', 'error');
        return;
      }
      
      // MARKET emri için anlık fiyatı kullan
      if (orderType === 'MARKET') {
        finalPrice = selectedAsset.current_price || livePrice;
      }
      
      showNotification('⚠️ Borsa kapalı - Emriniz bekleyen emir olarak kaydedilecek ve borsa açıldığında çalışacak', 'warning');
    } else {
      // BORSA AÇIK - Normal kontroller
      if (finalOrderType === 'LIMIT' && (!limitPrice || parseFloat(limitPrice) <= 0)) {
        showNotification('Geçerli bir limit fiyat girin', 'error');
        return;
      }
    }

    const amount = parseFloat(tradeAmount);
    const price = finalPrice;
    
    // Kaldıraç belirleme
    let actualLeverage = 1;
    if (orderType === 'LEVERAGE') {
      // KALDIRAÇ modunda kullanıcının seçtiği leverage'ı kullan
      actualLeverage = leverage;
    } else if ((selectedAsset?.category === 'VIOP' || selectedAsset?.category === 'KRIPTO') && tradingMode === 'LEVERAGE') {
      // VİOP ve KRİPTO için kullanıcının seçtiği kaldıraç
      actualLeverage = leverage;
    } else {
      // Diğer durumlar için otomatik
      actualLeverage = getAutomaticLeverage();
    }
    
    // USD varlıklar için TL'ye çevir, TRY varlıklar için direkt kullan
    // NOT: Kripto fiyatları veritabanında zaten TL cinsinde tutuluyor!
    const isUsdAsset = selectedAsset?.currency === 'USD' && selectedAsset?.category !== 'KRIPTO';
    const priceTRY = isUsdAsset ? (price * usdTryRate) : price;
    
    // Kaldıraçlı işlem için initial margin hesabı (%10)
    const positionSize = amount * priceTRY * actualLeverage;
    const requiredMargin = positionSize * 0.10; // %10 initial margin
    const totalCost = orderType === 'LEVERAGE' ? requiredMargin : (amount * priceTRY);

    if (action === 'BUY' && user.balance < totalCost) {
      showNotification('Yetersiz bakiye', 'error');
      return;
    }

    // Kaldıraç bilgisini göster - tradingMode LEVERAGE olduğunda her zaman göster
    const leverageInfo = (tradingMode === 'LEVERAGE' && actualLeverage > 1)
      ? `\nKaldıraç: ${actualLeverage}x\nPozisyon Boyutu: ${positionSize.toLocaleString('tr-TR', {minimumFractionDigits: 2})} TL\nGereken Margin: ${requiredMargin.toLocaleString('tr-TR', {minimumFractionDigits: 2})} TL` 
      : '';
    
    console.log('🔍 Trade Confirmation:', { 
      category: selectedAsset?.category,
      tradingMode, 
      manualLeverage: leverage,
      actualLeverage,
      leverageInfo,
      isUsdAsset,
      priceUSD: price,
      priceTRY,
      totalCost
    });
    
    // USD varlıklar için fiyat formatını düzelt
    const formattedPrice = isUsdAsset 
      ? `$${price.toFixed(2)} (₺${priceTRY.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})})`
      : `₺${price.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
    
    const formattedTotal = `₺${totalCost.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
    
    const message = `${action === 'BUY' ? 'Almak' : 'Satmak'} istediğinize emin misiniz?\n\n${!marketStatus.isOpen ? '⚠️ BORSA KAPALI\nEmriniz kaydedilecek ve borsa açılışında otomatik çalışacak\n\n' : ''}Varlık: ${selectedAsset.symbol}\nMiktar: ${amount}\nFiyat: ${formattedPrice}${leverageInfo}\nToplam: ${formattedTotal}`;

    showConfirmDialog(message, async () => {
      setLoading(true);
      try {
        // İşlem anında market durumunu tekrar kontrol et - en güncel değeri al
        const currentMarketStatus = isMarketOpen(selectedAsset.category);
        console.log('📊 Trade execution - Market status:', currentMarketStatus, 'is_pending:', !currentMarketStatus.isOpen);
        
        // Defensive validation: eksik alan varsa net hata ver
        // NOT: id yoksa symbol+category ile backend'e fallback gönderilecek
        if (!selectedAsset || (!selectedAsset.id && !selectedAsset.symbol)) {
          showNotification('Varlık bilgisi eksik. Lütfen listeden başka bir varlık seçin ve tekrar deneyin.', 'error');
          setLoading(false);
          return;
        }
        if (!action || (action !== 'BUY' && action !== 'SELL')) {
          showNotification('İşlem türü belirlenemedi (AL/SAT). Lütfen tekrar deneyin.', 'error');
          setLoading(false);
          return;
        }
        if (!Number.isFinite(amount) || amount <= 0) {
          showNotification('Geçerli bir miktar girin.', 'error');
          setLoading(false);
          return;
        }
        
        const tradeData = {
          asset_id: selectedAsset.id || null,
          asset_symbol: selectedAsset.symbol || null,  // Backend fallback için
          category: selectedAsset.category || null,     // Symbol disambiguation için
          transaction_type: action,
          amount: amount,
          trading_mode: tradingMode,
          leverage: actualLeverage, // Otomatik hesaplanan kaldıraç gönder
          order_type: finalOrderType,
          is_pending: !currentMarketStatus.isOpen, // Anlık kontrol kullan
          stop_loss: stopLoss ? parseFloat(stopLoss) : null,
          take_profit: takeProfit ? parseFloat(takeProfit) : null
        };
        console.log('📤 Trade payload:', tradeData);

        const response = await axios.post(`${API}/trade`, tradeData, axiosConfig);
        
        // Update user balance
        const userResponse = await axios.get(`${API}/auth/me`, axiosConfig);
        setUser(userResponse.data);
        
        // Clear form
        setTradeAmount('');
        setLimitPrice('');
        setStopLoss('');
        setTakeProfit('');
        
        // Trigger tabs refresh
        setRefreshTabs(prev => prev + 1);
        
        showNotification(
          !marketStatus.isOpen
            ? 'Bekleyen emir kaydedildi! Borsa açılışında (Pzt-Cum 10:00) otomatik çalışacak'
            : finalOrderType === 'MARKET' 
            ? 'İşlem anında gerçekleştirildi - Piyasa fiyatından alındı' 
            : 'Limit emir başarıyla oluşturuldu',
          'success'
        );
      } catch (err) {
        // Handle error message - convert objects to strings + Türkçe alan mapping
        const FIELD_TR = {
          asset_id: 'Varlık',
          transaction_type: 'İşlem türü (AL/SAT)',
          amount: 'Miktar',
          trading_mode: 'İşlem modu',
          leverage: 'Kaldıraç',
          order_type: 'Emir türü',
          limit_price: 'Limit fiyat',
          stop_loss: 'Zarar Durdur',
          take_profit: 'Kar Al',
          is_pending: 'Bekleyen emir durumu',
          price: 'Fiyat',
        };
        const translateMsg = (e) => {
          const rawMsg = e?.msg || '';
          const loc = Array.isArray(e?.loc) ? e.loc : [];
          const fieldRaw = loc.filter(x => x !== 'body').join('.') || 'alan';
          const field = FIELD_TR[fieldRaw] || fieldRaw;
          if (/field required/i.test(rawMsg) || e?.type === 'missing') return `${field} zorunlu (eksik gönderildi)`;
          if (/valid number/i.test(rawMsg)) return `${field} geçerli bir sayı olmalı`;
          if (/valid string/i.test(rawMsg)) return `${field} geçerli bir metin olmalı`;
          return `${field}: ${rawMsg}`;
        };
        
        let errorMessage = 'İşlem başarısız oldu';
        if (err.response?.data?.detail) {
          const detail = err.response.data.detail;
          if (typeof detail === 'string') {
            errorMessage = detail;
          } else if (Array.isArray(detail)) {
            errorMessage = detail.map(translateMsg).join('\n');
          } else if (typeof detail === 'object') {
            errorMessage = detail.msg || JSON.stringify(detail);
          }
        } else if (err.message) {
          errorMessage = err.message;
        }
        console.error('❌ Trade error:', err.response?.status, err.response?.data);
        showNotification(errorMessage, 'error');
      } finally {
        setLoading(false);
      }
    });
  };

  // Filter and group assets by category
  const filteredAssets = assets.filter(asset => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      asset.symbol.toLowerCase().includes(query) ||
      asset.name.toLowerCase().includes(query)
    );
  });

  // Özel sıralama: FAVORITES, BIST_100, VIOP, DOVIZ, EMTIA, ENDEKSLER, ABD_NASDAQ, KRIPTO
  const categoryOrder = {
    'FAVORITES': 1,
    'BIST_100': 2,
    'VIOP': 3,
    'DOVIZ': 4,
    'EMTIA': 5,
    'ENDEKSLER': 6,
    'ABD_NASDAQ': 7,
    'KRIPTO': 8
  };

  const groupedAssets = categories.map(cat => {
    // Special handling for FAVORITES
    if (cat.id === 'FAVORITES') {
      const config = CATEGORY_CONFIG[cat.id] || { name: cat.name, icon: Star };
      return {
        id: cat.id,
        name: config.name,
        icon: config.icon,
        assets: filteredAssets.filter(a => favorites.includes(a.id)),
        order: categoryOrder[cat.id] || 99
      };
    }
    
    // STOCKS kategorisini filtrele (HİSSELER)
    if (cat.id === 'STOCKS' || cat.id === 'HISSELER') {
      return null;
    }
    
    // Diğer tüm kategoriler için
    const config = CATEGORY_CONFIG[cat.id] || { name: cat.name, icon: Globe };
    
    return {
      id: cat.id,
      name: config.name,
      icon: config.icon,
      assets: filteredAssets.filter(a => a.category === cat.id),
      order: categoryOrder[cat.id] || 99
    };
  }).filter(cat => cat && cat.assets.length > 0).sort((a, b) => a.order - b.order);

  // LIST VIEW
  if (viewMode === 'list') {
    return (
      <div className="min-h-screen bg-gray-900 text-white">
        <Navbar user={user} setUser={setUser} />

        {/* Search Bar */}
        <div className="sticky top-16 bg-gray-800 border-b border-gray-700 p-4 z-40">
          <div className="max-w-7xl mx-auto">
            <div className="relative">
              <input
                type="text"
                placeholder="Hisse ara (örn: THYAO, Akbank)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-gray-700 text-white px-4 py-3 pl-12 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <svg 
                className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  ✕
                </button>
              )}
            </div>
            
            {/* Arama Sonuçları - Arama yapılınca direkt göster */}
            {searchQuery && filteredAssets.length > 0 && (
              <div className="mt-2 bg-gray-800 rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                <div className="text-xs text-gray-400 px-3 py-2 border-b border-gray-700">
                  {filteredAssets.length} sonuç bulundu
                </div>
                {filteredAssets.slice(0, 20).map(asset => {
                  const liveData = livePrices[asset.symbol];
                  const displayChangePercent = liveData?.change_percent !== undefined 
                    ? liveData.change_percent 
                    : (asset.change_percent || 0);
                  return (
                  <button
                    key={asset.id}
                    onClick={() => {
                      handleAssetClick(asset);
                      setSearchQuery('');
                    }}
                    className="w-full flex items-center justify-between px-3 py-3 hover:bg-gray-700 border-b border-gray-700 last:border-0"
                  >
                    <div className="text-left">
                      <div className="font-bold text-sm">{asset.symbol}</div>
                      <div className="text-xs text-gray-400">{asset.name}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm">{formatCurrency(liveData?.price || asset.current_price, user, true, asset.currency || getCurrencyForCategory(asset.category))}</div>
                      <div className={`text-xs ${displayChangePercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {displayChangePercent >= 0 ? '+' : ''}{displayChangePercent.toFixed(2)}%
                      </div>
                    </div>
                  </button>
                  );
                })}
              </div>
            )}
            
            {searchQuery && filteredAssets.length === 0 && (
              <div className="mt-2 text-sm text-gray-400 text-center py-4">
                "{searchQuery}" için sonuç bulunamadı
              </div>
            )}
            
            {/* Market Status Indicator - SADECE trading görünümünde ve varlık seçiliyse göster */}
            {/* Liste görünümünde asla gösterme! */}
          </div>
        </div>

        {/* Assets List */}
        <div className="p-4 space-y-6 page-content-with-nav">
          {/* Loading skeleton - İlk yüklemede göster */}
          {assetsLoading ? (
            <div className="space-y-4">
              <div className="text-center py-4">
                <div className="inline-flex items-center gap-2 text-blue-400">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Varlıklar yükleniyor...</span>
                </div>
              </div>
              {[1,2,3].map(i => (
                <div key={i} className="bg-gray-800 rounded-lg p-4 animate-pulse">
                  <div className="h-6 bg-gray-700 rounded w-1/3 mb-4"></div>
                  <div className="space-y-3">
                    {[1,2,3].map(j => (
                      <div key={j} className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="h-4 bg-gray-700 rounded w-1/4 mb-2"></div>
                          <div className="h-3 bg-gray-700 rounded w-1/2"></div>
                        </div>
                        <div className="text-right">
                          <div className="h-4 bg-gray-700 rounded w-20 mb-2"></div>
                          <div className="h-3 bg-gray-700 rounded w-16"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : groupedAssets.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🔍</div>
              <div className="text-xl text-gray-400">Sonuç bulunamadı</div>
              <div className="text-sm text-gray-500 mt-2">"{searchQuery}" için eşleşme yok</div>
            </div>
          ) : (
            groupedAssets.map(category => {
              const CategoryIcon = category.icon;
              return (
            <div key={category.id} className="bg-gray-800 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleCategory(category.id)}
                className="w-full bg-gray-700 px-4 py-3 flex items-center gap-2 hover:bg-gray-600 transition-colors"
              >
                <CategoryIcon size={20} className="text-blue-500" />
                <span className="font-bold text-base sm:text-lg whitespace-nowrap">{category.name}</span>
                <span className="ml-auto text-sm text-gray-400 whitespace-nowrap">{category.assets.length} varlık</span>
                <svg 
                  className={`w-5 h-5 transition-transform ${expandedCategories[category.id] ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {expandedCategories[category.id] && (
                <div className="divide-y divide-gray-700">
                  {assets.filter(a => a.category === category.id).map((asset, index) => (
                    <AssetRow 
                      key={asset.id}
                      asset={asset}
                      user={user}
                      favorites={favorites}
                      toggleFavorite={toggleFavorite}
                      handleAssetClick={handleAssetClick}
                      formatCurrency={formatCurrency}
                      livePrice={livePrices[asset.symbol]?.price}
                      liveChangePercent={livePrices[asset.symbol]?.change_percent}
                    />
                  ))}
                </div>
              )}
            </div>
          );
          })
          )}
        </div>

        {/* Custom Notification Modal */}
        <CustomNotification
          show={notification.show}
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification({ show: false, message: '', type: 'info' })}
        />

        {/* Custom Confirm Modal */}
        <CustomConfirm
          show={confirm.show}
          message={confirm.message}
          onConfirm={() => {
            if (confirm.onConfirm) confirm.onConfirm();
            setConfirm({ show: false, message: '', onConfirm: null });
          }}
          onCancel={() => setConfirm({ show: false, message: '', onConfirm: null })}
        />

        {/* Bottom Navigation - Global component App.js'de */}
      </div>
    );
  }

  // TRADING VIEW
  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Navbar user={user} setUser={setUser} />
      
      {/* Asset Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 py-3">
        <div className="flex items-center justify-between">
          <button
            onClick={handleBackToList}
            className="text-gray-400 hover:text-white transition-colors"
          >
            ← Geri
          </button>
          <div className="flex-1 text-center">
            <div className="font-bold text-xl">{selectedAsset?.symbol}</div>
            <div className="text-sm text-gray-400">{selectedAsset?.name}</div>
          </div>
          <div className="w-20"></div>
        </div>
        <div className="mt-2 flex items-center justify-center gap-4">
          {(() => {
            // Fiyat: livePrice (grafikten gelen) öncelikli, selectedAsset.current_price yedek
            const displayPrice = (livePrice && livePrice > 0) ? livePrice : (selectedAsset?.current_price || 0);
            // Değişim: selectedAsset.change_percent öncelikli (API'den gelen doğru değer)
            // livePriceChange sadece canlı güncelleme varsa kullanılacak
            let displayChange = selectedAsset?.change_percent;
            if (displayChange === undefined || displayChange === null) {
              displayChange = livePriceChange !== null ? livePriceChange : 0;
            }
            
            // Çok küçük fiyatlar için de "Fiyat yükleniyor" gösterme
            if (displayPrice === 0 || displayPrice === null || displayPrice === undefined) {
              return <div className="text-gray-400 text-sm animate-pulse">Fiyat yükleniyor...</div>;
            }
            
            return (
              <>
                <div className="text-2xl font-bold font-mono">
                  {/* Kripto için: USD fiyatı + TL karşılığı göster */}
                  {selectedAsset?.category === 'KRIPTO' ? (
                    <>
                      <span className="text-white">
                        {formatPrice(livePrice || selectedAsset?.current_price_usd || displayPrice, 'USD')}
                      </span>
                      <span className="text-sm text-gray-400 ml-2">
                        ({formatPrice((livePrice || selectedAsset?.current_price_usd || displayPrice) * usdTryRate, 'TRY')})
                      </span>
                    </>
                  ) : selectedAsset?.currency === 'USD' ? (
                    /* Diğer USD varlıklar */
                    <>
                      <span className="text-white">{formatPrice(displayPrice, 'USD')}</span>
                      <span className="text-sm text-gray-400 ml-2">
                        ({formatPrice(displayPrice * usdTryRate, 'TRY')})
                      </span>
                    </>
                  ) : (
                    /* TRY varlıklar - BIST, VIOP, DOVIZ, EMTIA */
                    <span className="text-white">{formatPrice(displayPrice, 'TRY')}</span>
                  )}
                </div>
                <div className={`text-lg font-semibold ${displayChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {displayChange >= 0 ? '+' : ''}
                  {displayChange.toFixed(2)}%
                </div>
              </>
            );
          })()}
        </div>
        {/* USD/TRY Kur gösterimi - sadece kripto ve USD varlıklar için */}
        {selectedAsset?.category === 'KRIPTO' && (
          <div className="text-center mt-1">
            <span className="text-xs text-gray-500">USD/TRY: ₺{usdTryRate.toFixed(2)}</span>
          </div>
        )}
      </div>

      {/* Market Status Banner - Sadece kripto için veya borsa kapalıysa göster */}
      {(marketStatus.reason === '7/24 Açık' || !marketStatus.isOpen) && (
        <div className="px-4 pt-4">
          <div className={`p-3 rounded-lg flex items-center gap-3 ${
            marketStatus.isOpen 
              ? 'bg-green-900/30 border border-green-700' 
              : 'bg-red-900/30 border border-red-700'
          }`}>
            <span className={`w-3 h-3 rounded-full flex-shrink-0 ${
              marketStatus.isOpen ? 'bg-green-500 animate-pulse' : 'bg-red-500'
            }`}></span>
            <div className="flex-1">
              <span className={`font-bold ${
                marketStatus.isOpen ? 'text-green-400' : 'text-red-400'
              }`}>
                {marketStatus.reason === '7/24 Açık' ? '🟢 Kripto - 7/24 Açık' : '🔴 Borsa Kapalı'}
              </span>
              <span className="text-gray-400 text-sm ml-2">- {marketStatus.reason}</span>
            </div>
            {!marketStatus.isOpen && (
              <span className="text-xs text-gray-500 whitespace-nowrap">
                BIST: Pzt-Cum 10:00-18:00
              </span>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="space-y-4 p-4 page-content-with-nav">
        {/* Chart and Trading Panel */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left: Chart */}
          <div className="lg:col-span-2">
            {/* Advanced Stock Chart with Real Data */}
            {selectedAsset && (
              <AdvancedStockChart 
                symbol={selectedAsset.symbol} 
                height={500}
                user={user}
                category={selectedAsset.category}
                asset={selectedAsset}
                currency={selectedAsset.currency}
                usdTryRate={usdTryRate}
                onPriceUpdate={(price, change, priceUsd) => {
                  // Grafik'ten gelen anlık fiyatı kullan - HER ZAMAN EN GÜNCEL
                  if (price && price > 0) {
                    // KRİPTO için: price = USD fiyatı (örn: $91,440)
                    const isKripto = selectedAsset?.category === 'KRIPTO';
                    console.log(`📊 Chart → Header: ${selectedAsset.symbol} = ${isKripto ? '$' : '₺'}${price.toFixed(2)} (${change?.toFixed(2)}%)`);
                    
                    // livePrice'a USD fiyatını set et
                    setLivePrice(price);
                    
                    if (change !== undefined && change !== null) {
                      setLivePriceChange(change);
                    }
                    
                    // selectedAsset'i de güncelle - current_price ve current_price_usd aynı olacak (KRİPTO için)
                    setSelectedAsset(prev => {
                      if (!prev) return prev;
                      return {
                        ...prev,
                        current_price: price,
                        current_price_usd: price, // KRİPTO için: USD fiyatı = price
                        change_percent: change !== undefined ? change : prev.change_percent
                      };
                    });
                  }
                }}
              />
            )}
            
            {!selectedAsset && (
              <div className="bg-gray-800 rounded-lg p-12 text-center">
                <BarChart2 className="w-20 h-20 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-xl font-semibold mb-2">Grafik Görüntüleme</p>
                <p className="text-gray-500 text-sm">Soldan bir varlık seçerek gerçek zamanlı grafik görebilirsiniz</p>
              </div>
            )}
          </div>
          
          {/* Right: Trading Panel (SPOT/Kaldıraç) */}
          <div className="space-y-4">
          {/* Market Status Warning for Trading Panel */}
          {!marketStatus.isOpen && (
            <div className="bg-yellow-900/30 border border-yellow-700 rounded-lg p-3">
              <div className="flex items-center gap-2 text-yellow-400">
                <span className="text-2xl">⚠️</span>
                <div className="flex-1">
                  <div className="font-bold text-sm mb-1">🔴 Borsa Kapalı - Emir Modu</div>
                  <div className="text-xs text-yellow-300 leading-relaxed">
                    • Tüm işlemler <strong>bekleyen emir</strong> olarak kaydedilir<br/>
                    • Borsa açılışında (<strong>Pzt-Cum 10:00</strong>) otomatik çalışır<br/>
                    • Borsa açıkken işlemler anlık gerçekleşir
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* 💎 TOPLAM VARLIK PANELİ - YENİ */}
          <div className="bg-gradient-to-r from-gray-800 to-gray-750 rounded-lg p-3 mb-4 border border-gray-700">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex flex-col bg-blue-900/30 rounded p-1">
                <span className="text-blue-300 text-xs">Ana Kasa</span>
                <span className="text-blue-400 font-bold text-lg">
                  ₺{totalEquity.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </span>
              </div>
              <div className="flex flex-col bg-green-900/30 rounded p-1">
                <span className="text-green-300 text-xs">Kullanılabilir</span>
                <span className="text-green-400 font-bold text-lg">
                  ₺{(user?.available_balance ?? user?.balance ?? 0).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-gray-400 text-xs">Bloke</span>
                <span className="text-yellow-400 font-bold">
                  ₺{(user?.locked_balance || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-gray-400 text-xs">Anlık K/Z</span>
                <span className={`font-bold ${totalPnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {totalPnL >= 0 ? '+' : ''}₺{totalPnL.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </span>
              </div>
            </div>
          </div>
          
          {/* Trading Panel */}
          <div className="bg-gray-800 rounded-lg p-4">
            {/* 3 Ana Sekme: AL/SAT | EMİR | KALDIRAÇ */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => {
                  setOrderType('MARKET');
                  setTradingMode('SPOT');
                }}
                className={`flex-1 py-2 rounded font-semibold text-sm transition-colors ${
                  orderType === 'MARKET' && tradingMode === 'SPOT'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                }`}
              >
                AL/SAT
              </button>
              <button
                onClick={() => {
                  setOrderType('LIMIT');
                  setTradingMode('SPOT');
                }}
                className={`flex-1 py-2 rounded font-semibold text-sm transition-colors ${
                  orderType === 'LIMIT' && tradingMode === 'SPOT'
                    ? 'bg-yellow-600 text-white'
                    : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                }`}
              >
                EMİR
              </button>
              {(selectedAsset?.category === 'VIOP' || selectedAsset?.category === 'KRIPTO') && (
                <button
                  onClick={() => {
                    setOrderType('MARKET');
                    setTradingMode('LEVERAGE');
                  }}
                  className={`flex-1 py-2 rounded font-semibold text-sm transition-colors ${
                    tradingMode === 'LEVERAGE'
                      ? 'bg-orange-600 text-white'
                      : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                  }`}
                >
                  KALDIRAÇ
                </button>
              )}
            </div>

            {/* Kaldıraç Seçimi - Sadece KALDIRAÇ sekmesinde göster */}
            {tradingMode === 'LEVERAGE' && (
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2 text-gray-400">Kaldıraç Oranı</label>
                <div className="grid grid-cols-6 gap-2">
                  {[2, 5, 10, 20, 50, 100].map((lev) => (
                    <button
                      key={lev}
                      onClick={() => setLeverage(lev)}
                      className={`py-2 rounded text-sm font-bold transition-colors ${
                        leverage === lev
                          ? 'bg-orange-600 text-white'
                          : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                      }`}
                    >
                      {lev}x
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Limit Price */}
            {orderType === 'LIMIT' && (
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2 text-gray-400">Limit Fiyat ({getCurrencySymbol(user)})</label>
                <input
                  type="number"
                  step="0.01"
                  value={limitPrice}
                  onChange={(e) => setLimitPrice(e.target.value)}
                  className="w-full bg-gray-700 text-white px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>
            )}

            {/* Amount */}
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2 text-gray-400">Miktar</label>
              <input
                type="number"
                step="0.01"
                value={tradeAmount}
                onChange={(e) => setTradeAmount(e.target.value)}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0.00"
              />
              
              {/* Real-time calculation display */}
              {tradeAmount > 0 && selectedAsset && (
                <div className="mt-3 p-4 bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg border border-gray-600">
                  <div className="space-y-2">
                    {/* Fiyat gösterimi - category ve currency'e göre */}
                    {selectedAsset.category === 'KRIPTO' ? (
                      <>
                        {/* KRİPTO: Fiyatlar USD cinsinden! */}
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-400">Miktar:</span>
                          <span className="text-white font-mono font-semibold">{parseFloat(tradeAmount).toFixed(4)}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-400">Birim Fiyat (USD):</span>
                          <span className="text-blue-400 font-mono">
                            ${(livePrice || selectedAsset.current_price_usd || selectedAsset.current_price).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-400">Birim Fiyat (TL):</span>
                          <span className="text-blue-400 font-mono">
                            ₺{((livePrice || selectedAsset.current_price_usd || selectedAsset.current_price) * usdTryRate).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </span>
                        </div>
                        <div className="border-t border-gray-700 pt-2 mt-2"></div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-300 font-semibold">USD Toplam:</span>
                          <span className="text-white font-mono font-bold text-lg">
                            ${(parseFloat(tradeAmount) * (livePrice || selectedAsset.current_price_usd || selectedAsset.current_price)).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </span>
                        </div>
                        <div className="flex justify-between items-center bg-green-900/30 p-2 rounded border border-green-700">
                          <span className="text-green-300 font-semibold">Kasadan Gidecek TL:</span>
                          <span className="text-green-400 font-mono font-bold text-xl">
                            ₺{(parseFloat(tradeAmount) * (livePrice || selectedAsset.current_price_usd || selectedAsset.current_price) * usdTryRate).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 text-center mt-1">
                          (USD/TRY Kur: ₺{usdTryRate.toFixed(4)})
                        </div>
                      </>
                    ) : selectedAsset.currency === 'USD' ? (
                      <>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-400">Miktar:</span>
                          <span className="text-white font-mono font-semibold">{parseFloat(tradeAmount).toFixed(4)}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-400">Birim Fiyat:</span>
                          <span className="text-blue-400 font-mono">${(selectedAsset.current_price || livePrice).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                        </div>
                        <div className="border-t border-gray-700 pt-2 mt-2"></div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-300 font-semibold">USD Toplam:</span>
                          <span className="text-white font-mono font-bold text-lg">
                            ${(parseFloat(tradeAmount) * (selectedAsset.current_price || livePrice)).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </span>
                        </div>
                        <div className="flex justify-between items-center bg-green-900/30 p-2 rounded border border-green-700">
                          <span className="text-green-300 font-semibold">Kasadan Gidecek TL:</span>
                          <span className="text-green-400 font-mono font-bold text-xl">
                            ₺{(parseFloat(tradeAmount) * (selectedAsset.current_price || livePrice) * usdTryRate).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 text-center mt-1">
                          (USD/TRY Kur: ₺{usdTryRate.toFixed(4)})
                        </div>
                      </>
                    ) : (
                      /* TRY varlıklar için basit gösterim - BIST, VIOP, DOVIZ, EMTIA */
                      <>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-400">Miktar:</span>
                          <span className="text-white font-mono font-semibold">{parseFloat(tradeAmount).toFixed(4)}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-400">Birim Fiyat:</span>
                          <span className="text-blue-400 font-mono">₺{(selectedAsset.current_price || livePrice).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                        </div>
                        <div className="border-t border-gray-700 pt-2 mt-2"></div>
                        <div className="flex justify-between items-center bg-green-900/30 p-2 rounded border border-green-700">
                          <span className="text-green-300 font-semibold">Toplam:</span>
                          <span className="text-green-400 font-mono font-bold text-xl">
                            ₺{(parseFloat(tradeAmount) * (selectedAsset.current_price || livePrice)).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Stop Loss - K/Z Bazlı */}
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2 text-gray-400">Zarar Durdur (₺) - İsteğe Bağlı</label>
              <input
                type="number"
                step="1"
                min="1"
                value={stopLoss}
                onChange={(e) => setStopLoss(e.target.value)}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Örn: 500 (500 TL zarar edince kapat)"
              />
              <p className="text-xs text-gray-500 mt-1">
                💡 Şu kadar TL zarar edince pozisyonu otomatik kapat
              </p>
            </div>

            {/* Take Profit - K/Z Bazlı */}
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2 text-gray-400">Kar Al (₺) - İsteğe Bağlı</label>
              <input
                type="number"
                step="1"
                min="1"
                value={takeProfit}
                onChange={(e) => setTakeProfit(e.target.value)}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Örn: 1000 (1000 TL kar edince kapat)"
              />
              <p className="text-xs text-gray-500 mt-1">
                💡 Şu kadar TL kar edince pozisyonu otomatik kapat
              </p>
            </div>

            {/* Buy/Sell Buttons */}
            <div className="grid grid-cols-2 gap-2">
              {tradingMode === 'LEVERAGE' ? (
                <>
                  {/* KALDIRAÇ Sekmesi - LONG/SHORT Pozisyon Açma */}
                  <button
                    onClick={() => executeTrade('BUY')}
                    disabled={loading}
                    className="bg-green-500 hover:bg-green-600 text-white font-bold py-3 rounded transition-colors disabled:opacity-50 flex flex-col items-center justify-center"
                  >
                    {loading ? 'İşleniyor...' : (
                      <>
                        <TrendingUp size={24} />
                        <span className="text-sm">LONG</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => executeTrade('SELL')}
                    disabled={loading}
                    className="bg-red-500 hover:bg-red-600 text-white font-bold py-3 rounded transition-colors disabled:opacity-50 flex flex-col items-center justify-center"
                  >
                    {loading ? 'İşleniyor...' : (
                      <>
                        <TrendingDown size={24} />
                        <span className="text-sm">SHORT</span>
                      </>
                    )}
                  </button>
                </>
              ) : (
                <>
                  {/* AL/SAT ve EMİR Sekmesi - Normal AL/SAT */}
                  <button
                    onClick={() => executeTrade('BUY')}
                    disabled={loading}
                    className="bg-green-500 hover:bg-green-600 text-white font-bold py-3 rounded transition-colors disabled:opacity-50"
                  >
                    {loading ? 'İşleniyor...' : 'AL'}
                  </button>
                  <button
                    onClick={() => executeTrade('SELL')}
                    disabled={loading}
                    className="bg-red-500 hover:bg-red-600 text-white font-bold py-3 rounded transition-colors disabled:opacity-50"
                  >
                    {loading ? 'İşleniyor...' : 'SAT'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Trading Tabs - Açık İşlemler, Bekleyen İşlemler, Tamamlanan İşlemler */}
      <div className="bg-gray-800 rounded-lg overflow-hidden mt-4 mb-24 sm:mb-20">
        {/* Tab Headers - Responsive */}
        <div className="flex border-b border-gray-700 overflow-x-auto scrollbar-hide">
          <button
            onClick={() => setActiveTab('open')}
            className={`flex-1 px-2 py-2 sm:px-4 sm:py-4 text-[10px] sm:text-sm font-semibold transition-colors whitespace-nowrap ${
              activeTab === 'open'
                ? 'text-white bg-gray-700 border-b-2 border-blue-500'
                : 'text-gray-400 hover:text-white hover:bg-gray-750'
            }`}
          >
            Açık
          </button>
          <button
            onClick={() => setActiveTab('pending')}
            className={`flex-1 px-2 py-2 sm:px-4 sm:py-4 text-[10px] sm:text-sm font-semibold transition-colors whitespace-nowrap ${
              activeTab === 'pending'
                ? 'text-white bg-gray-700 border-b-2 border-yellow-500'
                : 'text-gray-400 hover:text-white hover:bg-gray-750'
            }`}
          >
            ⏳ Bekleyen
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`flex-1 px-2 py-2 sm:px-4 sm:py-4 text-[10px] sm:text-sm font-semibold transition-colors whitespace-nowrap ${
              activeTab === 'completed'
                ? 'text-white bg-gray-700 border-b-2 border-green-500'
                : 'text-gray-400 hover:text-white hover:bg-gray-750'
            }`}
          >
            📊 Tamamlanan
          </button>
        </div>

        {/* Tab Content - MOBİL UYUMLU + BOTTOM NAV İÇİN PADDING */}
        <div className="p-2 sm:p-4 pb-20 sm:pb-4 max-h-[320px] sm:max-h-[500px] overflow-y-auto">
          {activeTab === 'open' && (
            <div>
              <OpenPositions 
                user={user} 
                selectedAsset={selectedAsset} 
                refresh={refreshTabs}
                onRefresh={handleRefresh}
                onAutoClose={() => {
                  // SL/TP ile pozisyon kapandığında tamamlananlara geç
                  setActiveTab('completed');
                  handleRefresh();
                }}
                onPositionClick={(position) => {
                  // Find asset by symbol
                  const symbol = position.asset_symbol || position.symbol;
                  console.log('🔍 Looking for asset:', symbol, 'in', assets.length, 'assets');
                  
                  // Search in all assets
                  const asset = assets.find(a => a.symbol === symbol);
                  
                  if (asset) {
                    console.log(' Found asset:', asset);
                    setSelectedAsset(asset);
                    // Scroll to top to show the chart
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  } else {
                    console.error(' Asset not found:', symbol);
                    console.log('Available assets:', assets.map(a => a.symbol).slice(0, 10));
                    
                    // Fallback: Create a minimal asset object with asset_id from position
                    setSelectedAsset({
                      id: position.asset_id,  // 🐛 FIX: preserve asset_id so trade payload is valid
                      symbol: symbol,
                      name: symbol,
                      category: position.category || 'BIST_100',
                      current_price: position.live_price || position.avg_buy_price || 0,
                      opening_price: position.avg_buy_price || 0,
                      change_percent: 0
                    });
                    console.log('⚠️ Created fallback asset for:', symbol, 'with id:', position.asset_id);
                  }
                }}
              />
            </div>
          )}
          
          {activeTab === 'pending' && (
            <div>
              <PendingOrders user={user} selectedAsset={selectedAsset} refresh={refreshTabs} />
            </div>
          )}
          
          {activeTab === 'completed' && (
            <div>
              <TradeHistory 
                user={user} 
                selectedAsset={selectedAsset} 
                refresh={refreshTabs}
                onRefresh={handleRefresh}
              />
            </div>
          )}
        </div>
      </div>
      </div>

      {/* Custom Notification Modal */}
      <CustomNotification
        show={notification.show}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ show: false, message: '', type: 'info' })}
      />

      {/* Custom Confirm Modal */}
      <CustomConfirm
        show={confirm.show}
        message={confirm.message}
        onConfirm={() => {
          if (confirm.onConfirm) confirm.onConfirm();
          setConfirm({ show: false, message: '', onConfirm: null });
        }}
        onCancel={() => setConfirm({ show: false, message: '', onConfirm: null })}
      />

      {/* Bottom Navigation - Global component App.js'de */}
    </div>
  );
}

export default TradingPageNew;
